<template>
  <div class="attach-delivery">
    <h4 v-if="!isRoSwatActive">{{ $t('routeOptimization.label.departureInfo') }}</h4>

    <b-card v-if="!isRoSwatActive">
      <div class="row g-3">
        <div v-if="shouldShowUserGroupInput" class="col-12 col-md-3">
          <FormulateInput
            v-bind="userGroupField"
            class="p-0"
            :value="formValues.user_group_id"
            :fullObject="fullObjects.user_group"
            @input="$emit('update:formValues', { key: 'user_group_id', value: $event })"
            @update:fullObject="$emit('update:fullObjects', { key: 'user_group', value: $event })"
          />
        </div>

        <div v-if="shouldShowUserGroupLabel" class="col-12 col-md-3">
          <label class="user-group-label">
            {{ $t('global.field.userGroup') }}
          </label>
          <div class="user-group-display">
            {{ fullObjects.user_group?.name || '-' }}
          </div>
        </div>

        <div class="col-12 col-md-3">
          <AddressValueHelp
            :value="formValues.start_address_id"
            :fullObject="fullObjects.start_address_id"
            @input="$emit('update:formValues', { key: 'start_address_id', value: $event })"
            @update:fullObject="
              $emit('update:fullObjects', { key: 'start_address_id', value: $event })
            "
          />
        </div>

        <div class="col-12 col-md-3 d-flex align-items-center">
          <McButton
            class="attach-delivery__search-delivery-btn"
            :text="$t('routeOptimization.action.searchDelivery')"
            @click="handleOpenTable"
          />
        </div>
      </div>
    </b-card>

    <h4>{{ $t('routeOptimization.wizard.selectDelivery') }}</h4>

    <McTable
      v-if="isShowTable"
      ref="mcTable"
      withCard
      searchable
      hasViewManager
      freezeColumnBetween
      :fields="fields"
      :apiClient="apiClient"
      :activeFilters="activeFilters"
      :hasDelete="false"
      :hasPriority="true"
      :stickyPriority="true"
      :stickyHeader="true"
      :stickyCheckbox="true"
      :initialPriorityIds="priorityIds"
      @handlePriorityChange="handlePriorityChange"
      @handleSelectValue="handleSelectDelivery"
      @listRes="handleListRes"
    >
      <template #beforeSearchBar>
        <b-dropdown
          v-if="selectedDOIds.length > 0"
          id="action-options"
          toggle-tag="div"
          toggle-class="p-0 bg-transparent"
        >
          <template #button-content>
            <McButton
              variant="secondary"
              icon="icon-outline_chevron-down"
              icon-position="right"
              :text="$t('global.field.action')"
            />
          </template>
          <b-dropdown-item v-if="shouldShowSetPriority" @click="handleSetPriority">
            <span>
              <i class="mc-icon icon-fill_star" />
            </span>
            {{ $t('global.action.setPriority') }}
          </b-dropdown-item>
          <b-dropdown-item v-if="shouldShowRemovePriority" @click="handleRemovePriority">
            <span>
              <i class="mc-icon icon-outline_star" />
            </span>
            {{ $t('global.action.removePriority') }}
          </b-dropdown-item>
        </b-dropdown>
        <McTableFilter
          :activeFilters="activeFilters"
          :parseFilterDataMappings="parseFilterDataMappings"
          @submit="handleChangeActiveFilters"
        >
          <template #content="{ formValues, updateFormValues, fullObjects, updateFullObjects }">
            <InputTableFilter
              :formValues="formValues"
              :fullObjects="fullObjects"
              @update:formValues="updateFormValues($event)"
              @update:fullObjects="updateFullObjects($event)"
            />
          </template>
        </McTableFilter>
      </template>
      <template #viewManager="{ handleSetDataFields, dataFields, initialFields }">
        <McFilterSelect
          :defaultColumnOrder="initialFields"
          :currentColumnOrder="dataFields"
          :saveToLocalStorage="false"
          entityType="RouteOptimizationDeliveryOrder"
          @setColumn="handleSetDataFields"
        />
      </template>
      <template #cell(number)="{ value, item }">
        <router-link
          class="text-secondary-route-link"
          :to="{ name: 'Delivery Detail', params: { id: item?.id } }"
        >
          {{ value || '-' }}
        </router-link>
      </template>
      <template #[`cell(status.name)`]="{ value, item }">
        <span
          v-if="item.status?.color"
          class="__status-dots"
          :style="{ backgroundColor: item.status.color }"
        ></span>
        <span class="mc-text-status">{{ setStatusTMS('delivery.status', value) }}</span>
      </template>
      <template #cell(line_order)="{ value }">
        {{ value === null || value === undefined || value === '' ? '-' : value }}
      </template>

      <template #cell(created_on)="{ value }">
        <div>{{ formatIndexDate(value).date }}</div>
        <div class="mc-datetime">
          {{ formatIndexDate(value).time }}
        </div>
      </template>
      <template #cell(expected_delivered_on)="{ value }">
        <div>{{ formatIndexDate(value).date }}</div>
        <div class="mc-datetime">
          {{ formatIndexDate(value).time }}
        </div>
      </template>
      <template #cell(expected_pickup_on)="{ value }">
        <div>{{ formatIndexDate(value).date }}</div>
        <div class="mc-datetime">
          {{ formatIndexDate(value).time }}
        </div>
      </template>
      <template #cell(product_categories)="{ value }">
        <McChipList
          v-if="value?.length"
          :items="value"
          :max-visible="3"
          itemTextField="name"
          showAllMode="dropdown"
          :showAllDropdownTitle="$t('setting.list.product.field.boxType')"
          showAllDropdownTrigger="action"
        />
      </template>
    </McTable>

    <b-card v-else>
      <div class="attach-delivery__illustration">
        <div>
          <figure>
            <img src="@/assets/images/illustration/whitelabel/no-data.svg" />
          </figure>
          <p
            class="description"
            v-html="$t('routeOptimization.emptyState.setOriginAddressFirst')"
          />
        </div>
      </div>
    </b-card>
  </div>
</template>

<script>
import McTable from '@/components/McTable'
import McButton from '@/components/McButton'
import McFilterSelect from '@/components/McFilterSelect'
import McTableFilter from '@/components/McTableFilter'
import McChipList from '@/components/McChipList'

import AddressValueHelp from '../wrapper/AddressValueHelp'
import InputTableFilter from '../components/InputTableFilter'

import * as Delivery from '@/services/rest-api/lib/route-optimization/delivery-order'

import getDOListFields from '../lib/fields/delivery-list-fields.js'
import parseFilterDataMappings from '../lib/constants/parse-filter-data-mappings'
import filterMappings from '../lib/constants/filter-mappings'
import { userGroupField } from '../lib/fields/user-group-field.js'

import { formatIndexDate } from '@/helpers/tzDate'
import { Helper } from '@/mixin/helper'
import { mapGetters } from 'vuex'
import { TableHelper } from '@/mixin/table'

export default {
  components: {
    McTable,
    McButton,
    McFilterSelect,
    AddressValueHelp,
    McTableFilter,
    InputTableFilter,
    McChipList,
  },
  mixins: [Helper, TableHelper],
  props: {
    formValues: {
      type: Object,
      default: () => {},
    },
    fullObjects: {
      type: Object,
      default: () => {},
    },
    isReoptimize: {
      type: Boolean,
      default: false,
    },
    orderReoptimzeIds: {
      type: Array,
      default: () => [],
    },
    highPriorityOrdersIds: {
      type: Array,
      default: () => [],
    },
    isRoSwatActive: {
      type: Boolean,
      default: false,
    },
    originAddressIdRoSwat: {
      type: Number,
      default: null,
    },
  },
  emits: ['update:formValues', 'update:fullObjects'],
  data() {
    return {
      isShowTable: false,
      activeFilters: {},
      isUserSelection: false,
      priorityIds: {},
      selectedPriorityIds: [],
      selectedDOIds: [],
    }
  },
  computed: {
    ...mapGetters({
      hasAccessAll: 'identity/hasAccessAll',
      getFeatureFlagValue: 'identity/getFeatureFlagValue',
    }),
    hasTmsUserGroupAccess() {
      return this.hasAccessAll(['tms-user-group:read']) && this.isSuperUser()
    },
    shouldShowUserGroupInput() {
      if (this.isReoptimize) {
        return false
      }

      return this.hasTmsUserGroupAccess
    },
    shouldShowUserGroupLabel() {
      if (!this.isReoptimize) {
        return false
      }

      return this.hasTmsUserGroupAccess && !!this.formValues.user_group_id
    },
    hasRoSwatOriginAddress() {
      return !this.isReoptimize && this.isRoSwatActive && this.originAddressIdRoSwat != null
    },
    resolvedOriginAddressId() {
      if (this.hasRoSwatOriginAddress) {
        return this.originAddressIdRoSwat
      }

      return this.formValues.start_address_id
    },
    apiClient() {
      const filters = ['status.name in (CONFIRMED RESCHEDULED)', 'vendor.id is null']

      if (this.resolvedOriginAddressId != null) {
        filters.push(`origin_address.id eq ${this.resolvedOriginAddressId}`)
      }

      if (this.hasTmsUserGroupAccess && this.formValues.user_group_id != null) {
        filters.push(`user_group.id eq ${this.formValues.user_group_id}`)
      }

      if (Object.keys(this.activeFilters).length > 0) {
        Object.keys(this.activeFilters).forEach((key) => {
          const filter = filterMappings[key]?.(key, this.activeFilters[key]) ?? undefined
          if (filter) {
            filters.push(filter)
          }
        })
      }

      return {
        get: Delivery.getIndex(filters),
      }
    },
    userGroupField() {
      return userGroupField(this)
    },
    fields() {
      return this.addStickyColumnByKeys(getDOListFields(this), ['number', 'status.name'])
    },
    parseFilterDataMappings() {
      return parseFilterDataMappings
    },
    selectedWithPriority() {
      return this.selectedDOIds.filter((id) => this.selectedPriorityIds.includes(id))
    },
    shouldShowSetPriority() {
      return this.selectedWithPriority.length < this.selectedDOIds.length
    },
    shouldShowRemovePriority() {
      return this.selectedWithPriority.length > 0
    },
    shouldShowTable() {
      if (this.isReoptimize) return true
      if (this.hasRoSwatOriginAddress) return true

      const hasStartAddress = this.resolvedOriginAddressId
      const hasDeliveryOrders = this.formValues.delivery_order_ids?.length > 0

      if (this.hasTmsUserGroupAccess) {
        const hasUserGroup = this.formValues.user_group_id != null
        return hasStartAddress && hasUserGroup && hasDeliveryOrders
      }

      return hasStartAddress && hasDeliveryOrders
    },
  },
  watch: {
    'formValues.start_address_id': function () {
      this.showTableBasedOnRequiredInput()
      this.clearSelectedData() // Clear selected data when start_address_id changes
    },

    'formValues.user_group_id': function () {
      if (this.hasTmsUserGroupAccess) {
        this.showTableBasedOnRequiredInput()
        this.clearSelectedData()
      }
    },

    'formValues.delivery_order_ids': {
      handler(newIds, oldIds) {
        if (newIds?.length > 0 && this.selectedDOIds.length === 0) {
          const isNewData = !oldIds || oldIds.length !== newIds.length

          if (isNewData) {
            this.selectedDOIds = [...newIds]

            if (this.resolvedOriginAddressId) {
              this.isShowTable = true
            }

            this.$nextTick(() => {
              this.setTableSelection(newIds)
            })
          }
        }
      },
      immediate: false,
    },

    orderReoptimzeIds: {
      handler(newIds) {
        if (this.isUserSelection) {
          this.isUserSelection = false
          return
        }

        if (newIds && newIds.length > 0) {
          this.$emit('update:formValues', {
            key: 'delivery_order_ids',
            value: [...newIds],
          })

          if (this.isShowTable) {
            this.$nextTick(() => {
              this.setTableSelection(newIds)
            })
          }
        } else {
          this.$emit('update:formValues', {
            key: 'delivery_order_ids',
            value: [],
          })
        }
      },
      immediate: false,
    },

    highPriorityOrdersIds: {
      handler(newIds) {
        if (newIds && newIds.length > 0) {
          const priorityIds = {}
          newIds.forEach((id) => {
            priorityIds[id] = true
          })
          this.priorityIds = { ...this.priorityIds, ...priorityIds }
          this.selectedPriorityIds = [...newIds]
        }
      },
      immediate: true,
    },

    isShowTable: {
      handler(isVisible) {
        if (isVisible && this.orderReoptimzeIds.length > 0) {
          this.$nextTick(() => {
            this.setTableSelection(this.orderReoptimzeIds)
          })
        }
      },
    },
  },
  mounted() {
    this.showTableBasedOnRequiredInput()
    this.initializeFromExistingData()
  },
  methods: {
    formatIndexDate,

    showTableBasedOnRequiredInput() {
      this.activeFilters = {}
      this.isShowTable = this.shouldShowTable
    },

    handleOpenTable() {
      if (!this.resolvedOriginAddressId) {
        this.$mcToast.error(this.$t('routeOptimization.alert.searchOriginAddressFirst'))
        return
      }

      if (this.hasTmsUserGroupAccess && this.formValues.user_group_id == null) {
        return
      }

      this.isShowTable = true
    },

    initializeFromExistingData() {
      const hasDeliveryOrders = this.formValues.delivery_order_ids?.length > 0
      const hasStartAddress = this.resolvedOriginAddressId

      if (!hasDeliveryOrders || !hasStartAddress) return

      this.selectedDOIds = [...this.formValues.delivery_order_ids]
      this.isShowTable = true

      this.$nextTick(() => {
        if (this.$refs.mcTable) {
          this.setTableSelection(this.formValues.delivery_order_ids)
        }
      })
    },

    clearSelectedData() {
      this.selectedDOIds = []

      if (this.$refs.mcTable) {
        this.$refs.mcTable.selectedIds = {}
        this.$refs.mcTable.selectedIdsLength = 0
      }
      this.$emit('update:formValues', { key: 'delivery_order_ids', value: [] })
    },

    setTableSelection(ids) {
      if (!this.$refs.mcTable || !ids || ids.length === 0) {
        return
      }

      const selectedIds = {}
      ids.forEach((id) => {
        selectedIds[id] = true
      })

      this.$set(this.$refs.mcTable, 'selectedIds', { ...selectedIds })
      this.$set(this.$refs.mcTable, 'selectedIdsLength', ids.length)

      this.selectedDOIds = [...ids]
    },

    updateDeliveryOrders() {
      if (!this.$refs.mcTable || !this.$refs.mcTable.selectedIds) return

      const selectedIds = this.$refs.mcTable.selectedIds
      const value = []

      for (const [key, val] of Object.entries(selectedIds)) {
        if (!val) continue
        value.push(key)
      }

      const delivery_orders = value.map((id) => {
        return {
          id: id,
          priority: this.selectedPriorityIds.includes(id) ? 'high' : 'regular',
        }
      })

      this.selectedDOIds = value

      this.$emit('update:formValues', { key: 'delivery_orders', value: delivery_orders })
      this.$emit('update:formValues', { key: 'delivery_order_ids', value })
    },

    handleSelectDelivery() {
      this.isUserSelection = true

      this.updateDeliveryOrders()
    },

    handleChangeActiveFilters(filters) {
      filters.forEach((filter) => {
        this.$delete(this.activeFilters, filter.key)

        if (filter.values === null || filter.values === undefined || filter.values === '') return
        if (Array.isArray(filter.values) && filter.values.length === 0) return

        this.$set(this.activeFilters, filter.key, filter.values)
      })
    },

    async handlePriorityChange(data) {
      this.priorityIds = data.priorityIds

      const value = []

      for (const [key, val] of Object.entries(data.priorityIds)) {
        if (!val) continue
        value.push(key)
      }

      this.selectedPriorityIds = value

      this.updateDeliveryOrders()
      // Save to backend if item is available
      if (data.item) {
        try {
          await Delivery.update({
            delivery_order_uuids: [data.item.id],
            priority: data.priority ? 'high' : 'regular',
          })
        } catch (error) {
          console.error('Failed to update priority:', error)
        }
      }
    },

    async handleSetPriority() {
      this.selectedDOIds.forEach((id) => {
        this.$set(this.priorityIds, id, true)
      })

      this.updateSelectedPriorityIds()

      this.updateDeliveryOrders()

      if (this.$refs.mcTable && this.$refs.mcTable.updatePriorityIds) {
        this.$refs.mcTable.updatePriorityIds(this.priorityIds)
      }

      // Save to backend
      try {
        await Delivery.update({
          delivery_order_uuids: this.selectedDOIds,
          priority: 'high',
        })
      } catch (error) {
        console.error('Failed to set priority:', error)
      }
    },

    async handleRemovePriority() {
      this.selectedDOIds.forEach((id) => {
        this.$set(this.priorityIds, id, false)
      })

      this.updateSelectedPriorityIds()

      this.updateDeliveryOrders()

      if (this.$refs.mcTable && this.$refs.mcTable.updatePriorityIds) {
        this.$refs.mcTable.updatePriorityIds(this.priorityIds)
      }
      // Save to backend
      try {
        await Delivery.update({
          delivery_order_uuids: this.selectedDOIds,
          priority: 'regular',
        })
      } catch (error) {
        console.error('Failed to remove priority:', error)
      }
    },

    updateSelectedPriorityIds() {
      const value = []

      for (const [key, val] of Object.entries(this.priorityIds)) {
        if (val) {
          value.push(key)
        }
      }

      this.selectedPriorityIds = value
    },

    handleListRes(res) {
      if (res?.data?.paginated_result) {
        const newPriorityIds = { ...this.priorityIds }
        res.data.paginated_result.forEach((item) => {
          newPriorityIds[item.id] = item.priority === 'high'
        })
        this.priorityIds = { ...newPriorityIds }
        this.updateSelectedPriorityIds()
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.attach-delivery {
  &__illustration {
    margin: 0;
    display: flex;
    justify-content: center;
    color: $mep-color-text-400;
  }

  .__status-dots {
    display: inline-block;
    margin: auto;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    margin-right: 8px;
  }
}

.user-group {
  &-label {
    color: $mep-color-text-300;
  }
}

:deep(#action-options) {
  .dropdown-menu {
    right: 0px;
    left: auto !important;
  }

  .dropdown-toggle {
    border: none;
  }
}
</style>
